import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { ISaleOrderListItem, SaleOrderInvoicingStatusEnum, SegmentEnum } from '../../types';

/**
 * Estado de facturación a clientes (facturas propias, no Netsuite).
 *
 * "Pendiente" solo se muestra en rojo cuando realmente urge facturar:
 * - Corpo se factura lo antes posible → pendiente siempre es urgente.
 * - El resto (ej. Leisure) se factura al finalizar el viaje → pendiente es
 *   neutro durante el viaje y pasa a urgente cuando isReturnDateReached.
 *
 * El sobre-facturado se detecta acá comparando invoicedAmount contra
 * saleAmount (la API clampea el pendiente a 0 y no expone este estado).
 */

const OVERINVOICED_TOLERANCE = 0.01;

interface InvoicingStatusView {
	label: string;
	color: string;
	detail?: string;
}

export const isOverinvoiced = (row: ISaleOrderListItem): boolean =>
	row.saleAmount !== undefined && row.saleAmount > 0 && row.invoicedAmount > row.saleAmount + OVERINVOICED_TOLERANCE;

const isPendingUrgent = (row: ISaleOrderListItem): boolean =>
	row.segmentType === SegmentEnum.CORPO || row.isReturnDateReached;

/**
 * ¿Tenemos con qué decidir si urge facturar?
 *
 * Corpo alcanza por sí solo: se factura lo antes posible, sin mirar fechas.
 * Para cualquier otro segmento hace falta además saber si el viaje terminó, y
 * ahí sí los dos datos tienen que estar.
 *
 * Hay pantallas que arman el row desde un DTO que no trae ninguno de los dos
 * (el detalle del cliente, sin ir más lejos): ahí no se puede afirmar ni que
 * urge ni que no. Sin esta guarda toda orden pendiente caía en la rama "aún no
 * corresponde" y decía "se factura al finalizar el viaje" incluso sobre
 * órdenes corpo.
 */
const puedeResolverUrgencia = (row: ISaleOrderListItem): boolean =>
	row.segmentType === SegmentEnum.CORPO || (row.segmentType !== undefined && row.isReturnDateReached !== undefined);

const resolveStatusView = (row: ISaleOrderListItem): InvoicingStatusView | null => {
	if (isOverinvoiced(row)) {
		return { label: '⚠️ Revisar (sobre-facturado)', color: '#d32f2f' };
	}

	switch (row.invoicingStatus) {
		case SaleOrderInvoicingStatusEnum.Total:
			return { label: '🟢 Total', color: '#2e7d32' };
		case SaleOrderInvoicingStatusEnum.Partial:
			return { label: '🟡 Parcial', color: '#ed6c02' };
		case SaleOrderInvoicingStatusEnum.Pending:
			if (isPendingUrgent(row)) {
				return {
					label: '🔴 Pendiente',
					color: '#d32f2f',
					detail:
						row.segmentType === SegmentEnum.CORPO
							? 'Corpo: facturar lo antes posible'
							: 'Viaje finalizado: corresponde facturar',
				};
			}
			if (!puedeResolverUrgencia(row)) {
				return {
					label: '⏳ Pendiente',
					color: 'text.secondary',
					detail: 'Abrí la orden para ver si ya corresponde facturarla',
				};
			}
			return {
				label: '⏳ Pendiente',
				color: 'text.secondary',
				detail: 'Aún no corresponde: se factura al finalizar el viaje',
			};
		default:
			return null;
	}
};

export const getSaleOrderInvoicingCellLabel = (row: ISaleOrderListItem): string => resolveStatusView(row)?.label ?? '-';

interface SaleOrderInvoicingStatusCellProps {
	row: ISaleOrderListItem;
}

const SaleOrderInvoicingStatusCell: React.FC<SaleOrderInvoicingStatusCellProps> = ({
	row,
}: SaleOrderInvoicingStatusCellProps) => {
	const view = resolveStatusView(row);

	if (!view) {
		return <>-</>;
	}

	const detailParts: string[] = [];
	if (view.detail) detailParts.push(view.detail);
	detailParts.push(`Facturado: USD ${row.invoicedAmount}`);
	if (row.pendingInvoicingAmount != null) {
		detailParts.push(`Pendiente de facturar: USD ${row.pendingInvoicingAmount}`);
	}
	if (isOverinvoiced(row) && row.saleAmount !== undefined) {
		detailParts.push(`Venta: USD ${row.saleAmount}`);
	}

	return (
		<Tooltip title={detailParts.join(' · ')} arrow>
			<Box sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', maxWidth: '100%' }}>
				<Typography variant="body2" sx={{ color: view.color, fontWeight: 500 }} noWrap>
					{view.label}
				</Typography>
			</Box>
		</Tooltip>
	);
};

export default SaleOrderInvoicingStatusCell;
